import jsPDF from 'jspdf';
import { formatAmount, formatDecimal, formatActivityLabel, capitalizeList } from './formatters';
import { compressImageSrc } from './image-compression';
import { COMPANY, getCompanyContactLines } from './company-info';
import hoferLogoSrc from '@assets/hofer/hofer-logo.png';
import { DEFAULT_INVOICE_PART_TITLES, INVOICE_PARTS, type InvoiceCoverPage, type InvoiceData } from "@shared/invoice";
import { findInvoiceLayoutWarnings, type InvoiceBlockId, type InvoiceBounds, type InvoiceLayoutReport } from "@shared/invoice-layout";
import { observeInvoiceBounds } from "./invoice-render-bounds";
import QRCode from "qrcode";
import { buildEpcPayment } from "@shared/invoice-payment";
import { renderFacadeReport } from "./facade-report-renderer";
import type { FacadeReportPhoto } from "./facade-report";
import { renderPdfDescription } from "./pdf-formatted-description";
import { offerPartsOverview } from "./offer-parts-overview";
import { calculateOfferParts, normalizePartSettings, offerPart, OPTIONAL_PART_NOTE, type OfferPart, type OfferPartSettings } from "./offer-parts";

export type QuotePdfVariant = "gegliedert" | "teil-a" | "gesamt";
export const QUOTE_PDF_VARIANTS: Array<{ value: QuotePdfVariant; label: string }> = [
  { value: "gegliedert", label: "Gegliedert (A/B/C)" },
  { value: "teil-a", label: "Nur Teil A" },
  { value: "gesamt", label: "Gesamt ohne Gliederung" },
];

export interface QuoteData {
  pdfExportVariant?: QuotePdfVariant;
  offerParts?: OfferPartSettings;
  documentType?: "quote" | "invoice" | "report";
  report?: { feedback: string; photos: FacadeReportPhoto[] };
  taxRate?: number;
  isPreview?: boolean;
  invoiceInfo?: {
    dueDate: string;
    servicePeriod: string;
    sourceQuoteNumber: string;
    sellerUid: string;
    customerUid: string;
    paymentTerms: string;
    coverPage?: InvoiceCoverPage;
    groupByPart?: boolean;
    partTitles?: InvoiceData["partTitles"];
  };
  projectName: string;
  customerName: string;
  customerAddress: string;
  quoteNumber?: string;
  /** ISO-Datum-String (YYYY-MM-DD) des Angebotsdatums. Fehlt → heutiges Datum. */
  quoteDate?: string;
  items: Array<{
    teil?: OfferPart;
    description: string;
    quantity: number;
    unit: string;
    unitPrice: number;
    total: number;
    sourceLabel?: string;
    /** Vergleichsvariante – wird im PDF angezeigt, aber NICHT in die Summe gerechnet. */
    variant?: boolean;
    /** Optionaler Beschreibungstext (z.B. aus Foto-Analyse übernommen) – wird unter der Positionszeile ausgegeben. */
    beschreibung?: string;
  }>;
  photos?: Array<{ preview?: string; file: File; displayName?: string; description?: string }>;
  measurements?: Array<{
    photoName: string;
    fileName?: string;
    preview?: string;
    /** Erfasste Menge (Einheit s. `unit`). */
    amount: number;
    /** Einheit der Menge (z.B. m², Stück, lfm, …). */
    unit: string;
  }>;
  analysisResults?: Array<{
    material: string;
    condition: string;
    recommendations: string[];
  }>;
  perPhoto?: Array<{
    photoLabel: string;
    fileName?: string;
    /** Erfasste Menge des Bildes. */
    amount?: number;
    /** Einheit der Menge. */
    unit?: string;
    /** Optionale Bildquelle (Data-URL oder URL) für ein Vorschaubild im Header. */
    preview?: string;
    positions: Array<{
      teil?: OfferPart;
      name: string;
      /** Für dieses Foto individuell angepasster Leistungstext. */
      beschreibung?: string;
      qty: number;
      unit: string;
      ep: number;
      total: number;
      /** Vergleichsvariante – wird angezeigt, aber NICHT in die Zwischensumme gerechnet. */
      variant?: boolean;
    }>;
    subtotal: number;
  }>;
  notes: string;
  validUntil: string;
  subtotal: number;
  tax: number;
  total: number;
  /** Projektbeschreibung aus Schritt 1 + 2 (Stammdaten). Optional. */
  projectDetails?: {
    activity?: string;            // Tätigkeit (project.type)
    objektbeschaffenheit?: string[];
    massnahmen?: string[];
    geschosszahl?: number | null;
    hoehe?: number | null;        // in Metern
    menge?: number | null;
    mengeneinheit?: string;
    /** Freitext-Leistungs-/Projektbeschreibung aus Schritt 1.
     *  Wird im Stammdaten-Block direkt vor der Leistungspositionen-Tabelle
     *  als eigener Absatz gerendert (Markdown-Überschriften, Hervorhebungen und Listen). */
    description?: string;
  };
}

/** Export-only copies: never change the editor's assignment, totals or invoices. */
export function prepareQuotePdfExport(data: QuoteData): QuoteData {
  if (data.documentType && data.documentType !== "quote") return data;
  const variant = data.pdfExportVariant ?? "gegliedert";
  if (variant === "gegliedert") return data;
  const included = (teil?: OfferPart) => variant === "teil-a" ? offerPart(teil) === "A" :
    (data.offerParts?.[offerPart(teil)]?.included !== false);
  const items = data.items.filter((item) => included(item.teil) && (variant !== "gesamt" || !item.variant));
  const perPhoto = data.perPhoto?.map((photo) => {
    const positions = photo.positions.filter((position) => included(position.teil) && (variant !== "gesamt" || !position.variant));
    return { ...photo, positions, subtotal: positions.reduce((sum, position) => position.variant ? sum : sum + position.total, 0) };
  }).filter((photo) => photo.positions.length > 0);
  const exportItems = variant === "gesamt" ? [
    ...items,
    ...(perPhoto?.flatMap((photo) => photo.positions.map((position) => ({
      description: position.beschreibung?.trim() || position.name,
      quantity: position.qty, unit: position.unit, unitPrice: position.ep, total: position.total,
      sourceLabel: `${photo.photoLabel}${photo.fileName ? ` · ${photo.fileName}` : ""}`,
    }))) ?? []),
  ] : items;
  const totals = calculateOfferParts([
    ...exportItems, ...(variant === "teil-a" ? perPhoto?.flatMap((photo) => photo.positions) ?? [] : []),
  ]);
  return {
    ...data, items: exportItems, perPhoto: variant === "gesamt" ? undefined : perPhoto,
    offerParts: variant === "teil-a" ? normalizePartSettings({
      A: { ...normalizePartSettings(data.offerParts).A, included: true },
    }) : undefined,
    quoteNumber: variant === "teil-a" && data.quoteNumber && !data.quoteNumber.endsWith("-A") ?
      `${data.quoteNumber}-A` : data.quoteNumber,
    subtotal: totals.subtotal, tax: totals.tax, total: totals.total,
  };
}

export function quotePdfFilename(quoteNumber: string, variant: QuotePdfVariant) {
  const number = quoteNumber.trim().replace(/[^A-Za-z0-9ÄÖÜäöüß._-]+/g, "_") || "ohne_Nummer";
  const suffix = variant === "teil-a" ? "TeilA" : variant;
  return `Angebot_${number}_${suffix}.pdf`;
}

function getImageFormat(dataUrl: string): string {
  if (dataUrl.startsWith('data:image/png')) return 'PNG';
  if (dataUrl.startsWith('data:image/webp')) return 'WEBP';
  return 'JPEG';
}

// Hofer-Farbschema (PDF in RGB):
//   Teal #39ACAD  → (57, 172, 173)
//   Orange #FF8C00 → (255, 140, 0) – CTA-Akzent
//   Mint #D6ECEC  → (214, 236, 236) – helle Box-Konturen
//   Alt   #F2F8F8 → (242, 248, 248) – Zebra-Streifen-Hintergrund
const TEAL_R = 57, TEAL_G = 172, TEAL_B = 173;
const ORANGE_R = 255, ORANGE_G = 140, ORANGE_B = 0;
// Slate-Grau (#44556E) – passt zur Hofer-Wortmarke und wird im PDF-Header
// und Seitenfuß für den Markenschriftzug verwendet.
const SLATE_R = 68, SLATE_G = 85, SLATE_B = 110;
const MINT_R = 214, MINT_G = 236, MINT_B = 236;
const ALT_R = 242, ALT_G = 248, ALT_B = 248;

export interface PdfLayoutOptions {
  /** Seitenrand (links/rechts und Fallback oben). Default 15 mm. */
  margin?: number;
  /** Abstand zwischen Header-Banner und Inhalt. Default 2 mm. */
  headerContentGap?: number;
  /** Abstand unterhalb des Adress-/Infobox-Blocks. Angebote: 20 mm, Rechnungen: 8 mm. */
  addressBottomGap?: number;
  /** Zusätzlicher Abstand vor dem „ANGEBOT"-Titel. Default 0 mm. */
  titleTopGap?: number;
  /** Zusätzlicher Abstand vor dem Leistungsbeschreibungs-Block. Default 0 mm. */
  descriptionTopSpacing?: number;
}

export const DEFAULT_PDF_LAYOUT: Required<PdfLayoutOptions> = {
  margin: 15,
  headerContentGap: 2,
  addressBottomGap: 20,
  titleTopGap: 0,
  descriptionTopSpacing: 0,
};

/** Reusable invoice default; the quote layout and logo sizing stay unchanged. */
export const DEFAULT_INVOICE_PDF_LAYOUT: Required<PdfLayoutOptions> = {
  ...DEFAULT_PDF_LAYOUT,
  addressBottomGap: 8,
};

export class PDFGenerator {
  private coverBounds?: ReturnType<typeof observeInvoiceBounds>;
  private coverHeaderAreas: InvoiceBounds[] = [];
  private coverPageCount = 1;
  private invoiceLayoutReport?: InvoiceLayoutReport;

  getInvoiceLayoutReport() { return this.invoiceLayoutReport; }

  private pdf!: jsPDF;
  private pageHeight!: number;
  private pageWidth!: number;
  private currentY!: number;
  private margin = DEFAULT_PDF_LAYOUT.margin;
  private layout: Required<PdfLayoutOptions> = { ...DEFAULT_PDF_LAYOUT };
  private contentWidth!: number;
  private hoferLogoDataUrl: string | null = null;
  private hoferLogoAspect: number | null = null;

  private readonly cols = {
    pos:   { x: 0,   w: 8  },
    desc:  { x: 8,   w: 80 },
    qty:   { x: 88,  w: 22 },
    unit:  { x: 110, w: 20 },
    ep:    { x: 130, w: 25 },
    total: { x: 155, w: 25 },
  };

  private col(name: keyof typeof this.cols) {
    const c = this.cols[name];
    return { x: this.margin + c.x, w: c.w };
  }

  private newDoc() {
    this.pdf = new jsPDF({ unit: 'mm', format: 'a4' });
    this.pageHeight = this.pdf.internal.pageSize.height;
    this.pageWidth = this.pdf.internal.pageSize.width;
    this.contentWidth = this.pageWidth - 2 * this.margin;
    this.currentY = 0;
  }

  private async loadLogo(): Promise<void> {
    // Im Rebrand wird ausschließlich das offizielle Hofer-Logo verwendet
    // (Briefkopf, Footer-Markenzeile). Es wird einmal geladen, das natürliche
    // Seitenverhältnis bestimmt und für proportionsgetreue Skalierung gemerkt.
    await this.loadHoferLogoFromAssets();
  }

  /**
   * Lädt das Hofer-Logo aus den Assets, ermittelt sein natürliches
   * Seitenverhältnis (für unverzerrte Skalierung) und legt eine
   * komprimierte Data-URL für die jsPDF-Einbettung an.
   */
  private async loadHoferLogoFromAssets(): Promise<void> {
    try {
      const aspect = await new Promise<number>((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          const w = img.naturalWidth || img.width;
          const h = img.naturalHeight || img.height;
          if (!w || !h) {
            reject(new Error('Ungültige Hofer-Logo-Dimensionen'));
            return;
          }
          resolve(w / h);
        };
        img.onerror = () => reject(new Error('Hofer-Logo konnte nicht geladen werden'));
        img.src = hoferLogoSrc;
      });
      const dataUrl = await compressImageSrc(hoferLogoSrc, 600, 0.92);
      this.hoferLogoDataUrl = dataUrl;
      this.hoferLogoAspect = aspect;
    } catch (error) {
      console.warn('Hofer-Logo-Komprimierung fehlgeschlagen:', error);
      this.hoferLogoDataUrl = null;
      this.hoferLogoAspect = null;
    }
  }

  private checkPageBreak(needed: number) {
    if (this.currentY + needed > this.pageHeight - 34) {
      this.pdf.addPage();
      this.currentY = this.margin;
    }
  }

  /** Runs the actual cover renderer, without fetching photos or drawing attachments. */
  async measureInvoiceLayout(data: QuoteData): Promise<InvoiceLayoutReport> {
    await this.startDocument({ ...data, documentType: "invoice" });
    return this.invoiceLayoutReport!;
  }

  private async startDocument(data: QuoteData, options?: PdfLayoutOptions): Promise<QuoteData> {
    this.layout = {
      ...(data.documentType === "invoice" ? DEFAULT_INVOICE_PDF_LAYOUT : DEFAULT_PDF_LAYOUT),
      ...(options ?? {}),
    };
    this.margin = this.layout.margin;
    this.newDoc();
    await this.loadLogo();
    this.documentType = data.documentType ?? "quote";
    this.coverBounds = this.documentType === "invoice" ? observeInvoiceBounds(this.pdf) : undefined;
    this.invoiceLayoutReport = undefined;
    this.coverHeaderAreas = [];
    this.taxRate = data.taxRate ?? 20;
    const printableItems = data.items.filter((item) => item.description.trim().length > 0);
    const printableData: QuoteData = printableItems.length === data.items.length
      ? data
      : { ...data, items: printableItems };

    this.addHeader(this.documentType === "quote" && data.pdfExportVariant === "teil-a");
    this.coverHeaderAreas.push({ page: 1, x: 0, y: 0, width: this.pageWidth, height: this.currentY - 4.3 });
    this.addAddressSection(printableData);
    if (this.documentType === "invoice") {
      this.addInvoiceCover(printableData);
      this.coverPageCount = this.pdf.getNumberOfPages();
      const blocks = this.coverBounds!.blocks;
      this.invoiceLayoutReport = {
        blocks,
        pageCount: this.coverPageCount,
        warnings: findInvoiceLayoutWarnings(blocks, [
          ...this.coverHeaderAreas.map((area) => ({ ...area, id: "header" as const })),
          ...Array.from({ length: this.coverPageCount }, (_, i) => ({
            id: "footer" as const, page: i + 1, x: 0, y: this.pageHeight - 29.5,
            width: this.pageWidth, height: 29.5,
          })),
        ], this.pageWidth, this.pageHeight),
      };
    }
    else this.addProjectDetails(printableData);
    return printableData;
  }

  async generateQuotePDF(data: QuoteData, options?: PdfLayoutOptions): Promise<Blob> {
    data = prepareQuotePdfExport(data);
    const printableData = await this.startDocument(data, options);
    if (this.documentType === "report") {
      if (!data.report) throw new Error("Berichtsdaten fehlen.");
      await renderFacadeReport({
        pdf: this.pdf, margin: this.margin, width: this.contentWidth, pageHeight: this.pageHeight,
        startY: this.currentY,
        newPage: () => {
          this.pdf.addPage();
          this.currentY = 0;
          this.addHeaderBanner();
          return this.currentY + 6;
        },
        compressImage: (src) => this.compressImage(src),
        imageDimensions: (src) => this.getImageDimensions(src),
      }, data.report.feedback, data.report.photos);
      this.addFooterAllPages();
      return this.pdf.output("blob");
    }
    const printableItems = printableData.items;

    // Leistungspositionen beginnen nur dann auf einer frischen Seite, wenn
    // tatsächlich mindestens eine benannte Position vorhanden ist. So entsteht
    // bei leeren Angeboten keine unbedruckte Leistungspositions-Seite.
    //
    // Wenn vorhanden, beginnen sie IMMER auf einer frischen Seite (im
    // Standardfall Seite 2). Damit erscheinen die grüne LEISTUNGSPOSITIONEN-
    // Kopfleiste und die Tabellen-Kopfzeile nie unten auf Seite 1, sondern
    // konsistent oben auf einer neuen Seite — analog zum Briefkopf-Banner
    // auf Seite 1, aber ohne erneuten „ANGEBOT"-Titel.
    const parts = calculateOfferParts([
      ...data.items, ...(data.perPhoto?.flatMap((photo) => photo.positions) ?? []),
    ], data.offerParts);
    const groupedOffer = this.documentType === "quote" && parts.grouped &&
      data.pdfExportVariant !== "gesamt" && data.pdfExportVariant !== "teil-a";
    const groupedInvoice = this.documentType === "invoice" && data.invoiceInfo?.groupByPart === true;
    if (groupedInvoice && printableItems.length > 0) {
      this.addInvoiceParts(printableData);
    } else if (groupedOffer) {
      await this.addOfferParts(data);
    } else if (printableItems.length > 0) {
      this.pdf.addPage();
      this.currentY = 0;
      this.addHeaderBanner();
      if (this.documentType === "quote" && data.pdfExportVariant !== "gesamt") {
        this.addOfferPartDescription(parts.parts.find((part) => part.id === "A")?.description);
      }
      if (parts.parts.some((part) => !part.included)) {
        this.pdf.setFont("helvetica", "normal");
        this.pdf.setFontSize(8);
        this.pdf.setTextColor(80, 70, 40);
        this.pdf.text(OPTIONAL_PART_NOTE, this.margin, this.currentY + 5);
        this.currentY += 10;
      }
      this.addItemsTable(printableData);
    }

    if (!groupedOffer && printableData.perPhoto && printableData.perPhoto.length > 0) {
      if (!printableItems.length && this.documentType === "quote" && data.pdfExportVariant !== "gesamt") {
        this.addOfferPartDescription(parts.parts.find((part) => part.id === "A")?.description);
      }
      if (!printableItems.length && parts.parts.some((part) => !part.included)) {
        this.checkPageBreak(10);
        this.pdf.setFontSize(8);
        this.pdf.text(OPTIONAL_PART_NOTE, this.margin, this.currentY + 5);
        this.currentY += 10;
      }
      await this.addPerPhotoBreakdown(printableData);
    }

    if (printableData.measurements && printableData.measurements.length > 0) {
      this.pdf.addPage();
      this.currentY = this.margin;
      await this.addMeasurementPhotos(printableData.measurements);
    }
    if (printableData.photos && printableData.photos.length > 0) {
      this.pdf.addPage();
      this.currentY = this.margin;
      await this.addPhotos(printableData.photos);
    }
    if (printableData.analysisResults && printableData.analysisResults.length > 0) {
      this.addAnalysisResults(printableData.analysisResults);
    }
    if (printableData.notes && this.documentType !== "invoice") {
      this.addNotes(printableData.notes);
    }

    this.addFooterAllPages();
    return this.pdf.output('blob');
  }

  /**
   * Rendert den schlanken Briefkopf der Hofer GesmbH am Seitenanfang:
   * Hofer-Logo links, dreizeiliger Kontaktblock rechts, dünne Teal-Trennlinie.
   * Setzt `currentY` direkt unter den Trennstrich, damit Folgeinhalte sauber
   * andocken. Wird auf jeder Seite verwendet (Seite 1 zusätzlich mit dem
   * „ANGEBOT"-Titel via `addHeader()`).
   *
   * @returns immer true – im Fallback (Logo nicht geladen) wird wenigstens
   *   die Kontaktzeile als reine Textzeile gezeichnet.
   */
  private addHeaderBanner(): boolean {
    const topY = 12;
    const logoMaxH = 16;
    const logoMaxW = 60;
    let usedH = logoMaxH;

    if (this.hoferLogoDataUrl && this.hoferLogoAspect) {
      // Logo proportional in den verfügbaren Rahmen einpassen.
      let logoW = logoMaxH * this.hoferLogoAspect;
      let logoH = logoMaxH;
      if (logoW > logoMaxW) {
        logoW = logoMaxW;
        logoH = logoMaxW / this.hoferLogoAspect;
      }
      try {
        this.pdf.addImage(
          this.hoferLogoDataUrl,
          getImageFormat(this.hoferLogoDataUrl),
          this.margin,
          topY,
          logoW,
          logoH,
          undefined,
          'FAST',
        );
        usedH = logoH;
      } catch {
        /* Fall-through zur Text-Variante unten */
      }
    }

    // Markenschriftzug rechts: bewusst nur „FassadenCheck · Hofer GesmbH"
    // plus Tagline. Die volle Postanschrift (Treffling 75b, 9871 Seeboden,
    // Tel/Mail) wird auf Seite 1 separat im Briefkopf-Sender (siehe
    // `addAddressSection`) gezeigt und gehört NICHT zusätzlich in jeden
    // Seitenkopf, sonst doppelt sich die Information auf jeder PDF-Seite.
    this.pdf.setTextColor(SLATE_R, SLATE_G, SLATE_B);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setFontSize(9);
    this.pdf.text(
      `${COMPANY.name} · ${COMPANY.legalName}`,
      this.pageWidth - this.margin,
      topY + 4,
      { align: 'right' },
    );
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setFontSize(7.5);
    this.pdf.setTextColor(SLATE_R, SLATE_G, SLATE_B);
    const taglineLines = this.pdf.splitTextToSize(COMPANY.tagline, 98) as string[];
    this.pdf.text(
      taglineLines,
      this.pageWidth - this.margin,
      topY + 8,
      { align: 'right' },
    );

    // Feine Teal-Trennlinie unterhalb des Header-Bereichs.
    const lineY = topY + Math.max(usedH, 8 + taglineLines.length * 3.2) + 3;
    this.pdf.setDrawColor(TEAL_R, TEAL_G, TEAL_B);
    this.pdf.setLineWidth(0.4);
    this.pdf.line(this.margin, lineY, this.pageWidth - this.margin, lineY);

    this.currentY = lineY + this.layout.headerContentGap + 2;
    return true;
  }

  private documentType: "quote" | "invoice" | "report" = "quote";
  private taxRate = 20;

  private addHeader(partialOffer = false) {
    // Schlanker Hofer-Briefkopf + großer „ANGEBOT"-Titel auf Seite 1.
    this.addHeaderBanner();

    this.pdf.setTextColor(TEAL_R, TEAL_G, TEAL_B);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setFontSize(this.documentType === "report" ? 26 : 30);
    const angebotY = this.currentY + 10 + this.layout.titleTopGap;
    this.pdf.text(this.documentType === "invoice" ? 'RECHNUNG' : this.documentType === "report" ? 'FASSADENCHECK' : partialOffer ? 'TEILANGEBOT' : 'ANGEBOT', this.margin, angebotY);
    this.pdf.setDrawColor(ORANGE_R, ORANGE_G, ORANGE_B);
    this.pdf.setLineWidth(1.4);
    this.pdf.line(this.margin, angebotY + 2.4, this.margin + 42, angebotY + 2.4);
    this.currentY = angebotY + 7;
  }

  private addAddressSection(data: QuoteData) {
    const startY = this.currentY;

    this.pdf.setTextColor(160, 160, 160);
    this.pdf.setFontSize(6.5);
    this.pdf.setFont('helvetica', 'normal');
    const senderLines = getCompanyContactLines();
    const senderLineGap = 2.6;
    senderLines.forEach((line, i) => {
      this.pdf.text(line, this.margin, this.currentY + i * senderLineGap);
    });
    const lastSenderY = this.currentY + (senderLines.length - 1) * senderLineGap;
    this.coverHeaderAreas.push({ page: 1, x: this.margin, y: startY - 2,
      width: 95, height: lastSenderY - startY + 3.3 });

    this.pdf.setDrawColor(210, 210, 210);
    this.pdf.setLineWidth(0.2);
    this.pdf.line(this.margin, lastSenderY + 1.2, this.margin + 95, lastSenderY + 1.2);

    this.currentY = lastSenderY + 10.6;
    const addressOffset = data.documentType === "invoice" ? data.invoiceInfo?.coverPage?.layout?.address : undefined;
    const addressX = Math.min(this.pageWidth - 93, Math.max(5, this.margin + (addressOffset?.x ?? 0)));
    this.currentY = Math.min(this.pageHeight - 70, Math.max(40, this.currentY + (addressOffset?.y ?? 0)));

    // Maximal verfügbare Breite für Kunden-Name/Adresse, damit nichts in
    // die rechte Infobox (startet bei margin + 95) hineinläuft.
    const addressMaxW = 88;
    this.coverBounds?.activate("address");

    this.pdf.setTextColor(0, 0, 0);
    this.pdf.setFontSize(13);
    this.pdf.setFont('helvetica', 'bold');
    const nameLines = this.pdf.splitTextToSize(data.customerName || '—', addressMaxW);
    nameLines.slice(0, 2).forEach((line: string) => {
      this.pdf.text(line, addressX, this.currentY);
      this.currentY += 5.6;
    });
    this.currentY += 0.4;

    if (data.customerAddress) {
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.setFontSize(10);
      data.customerAddress.split('\n').forEach(line => {
        if (!line.trim()) return;
        const wrapped = this.pdf.splitTextToSize(line, addressMaxW);
        wrapped.forEach((wl: string) => {
          this.pdf.text(wl, addressX, this.currentY);
          this.currentY += 4.8;
        });
      });
    }

    this.coverBounds?.activate("documentInfo");
    const infoOffset = data.documentType === "invoice" ? data.invoiceInfo?.coverPage?.layout?.documentInfo : undefined;
    const boxX = Math.min(this.pageWidth - 90, Math.max(5, this.margin + 95 + (infoOffset?.x ?? 0)));
    const boxW = 85;
    const boxY = Math.min(this.pageHeight - 100, Math.max(35, startY - 1 + (infoOffset?.y ?? 0)));
    const quoteDateStr = data.quoteDate
      ? new Date(data.quoteDate + 'T12:00:00').toLocaleDateString('de-DE')
      : new Date().toLocaleDateString('de-DE');
    const infoRows: [string, string][] = [
      ...(this.documentType === "report" ? [] : [[this.documentType === "invoice" ? 'Belegnummer:' : 'Angebots-Nr.:', data.quoteNumber || '—'] as [string, string]]),
      ['Datum:', quoteDateStr],
      ...(this.documentType === "invoice"
        ? [
            ...(data.invoiceInfo?.coverPage?.customerNumber ? [['Kundennummer:', data.invoiceInfo.coverPage.customerNumber] as [string, string]] : []),
            ...(data.invoiceInfo?.dueDate ? [['Fällig am:', new Date(data.invoiceInfo.dueDate + 'T12:00:00').toLocaleDateString('de-DE')] as [string, string]] : []),
            ...(data.invoiceInfo?.sourceQuoteNumber ? [['Angebot:', data.invoiceInfo.sourceQuoteNumber] as [string, string]] : []),
          ]
        : this.documentType === "report" ? [] : [['Gültig bis:', new Date(data.validUntil).toLocaleDateString('de-DE')] as [string, string]]),
      ['Projekt:', data.projectName || '—'],
    ];
    const rowH = 6.5;
    this.pdf.setFontSize(8);
    this.pdf.setFont('helvetica', 'bold');
    const valueLines: string[][] = infoRows.map(([, value]) =>
      this.documentType !== "quote"
        ? this.pdf.splitTextToSize(String(value), boxW - 36)
        : [String(value).length > 24 ? String(value).slice(0, 22) + '…' : String(value)]);
    const rowHeights = valueLines.map((lines) => Math.max(rowH, lines.length * 4.2 + 2));
    const boxH = 9 + rowHeights.reduce((sum, height) => sum + height, 0);

    this.pdf.setFillColor(248, 251, 251);
    this.pdf.setDrawColor(MINT_R, MINT_G, MINT_B);
    this.pdf.setLineWidth(0.4);
    this.pdf.roundedRect(boxX, boxY, boxW, boxH, 2, 2, 'FD');

    this.pdf.setFillColor(TEAL_R, TEAL_G, TEAL_B);
    this.pdf.roundedRect(boxX, boxY, boxW, 8, 2, 2, 'F');
    this.pdf.rect(boxX, boxY + 5, boxW, 3, 'F');
    this.pdf.setTextColor(255, 255, 255);
    this.pdf.setFontSize(7.5);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text(this.documentType === "invoice" ? 'RECHNUNGSINFORMATIONEN' : this.documentType === "report" ? 'BERICHTSINFORMATIONEN' : 'ANGEBOTSINFORMATIONEN', boxX + boxW / 2, boxY + 5.5, { align: 'center' });

    let ry = boxY + 12;
    infoRows.forEach(([label], index) => {
      this.pdf.setTextColor(100, 110, 110);
      this.pdf.setFontSize(8);
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.text(label, boxX + 4, ry);

      this.pdf.setTextColor(20, 20, 20);
      this.pdf.setFont('helvetica', 'bold');
      valueLines[index].forEach((line, lineIndex) =>
        this.pdf.text(line, boxX + boxW - 4, ry + lineIndex * 4.2, { align: 'right' }));
      ry += rowHeights[index];
    });

    this.coverBounds?.activate();
    this.currentY -= addressOffset?.y ?? 0;
    const boxBottom = boxY + boxH - (infoOffset?.y ?? 0);
    if (this.currentY < boxBottom) this.currentY = boxBottom;
    this.currentY += this.layout.addressBottomGap;
  }

  private addProjectDetails(data: QuoteData) {
    const d = data.projectDetails;
    if (!d) return;
    const hasObjekt = (d.objektbeschaffenheit ?? []).length > 0;
    const hasMassn = (d.massnahmen ?? []).length > 0;
    const hasGeschoss = d.geschosszahl != null;
    const hasHoehe = d.hoehe != null;
    const hasMenge = d.menge != null;
    const descriptionText = (d.description ?? '').trim();
    const hasDescription = descriptionText.length > 0;
    if (
      !d.activity && !hasObjekt && !hasMassn && !hasGeschoss && !hasHoehe && !hasMenge && !hasDescription
    ) return;

    this.checkPageBreak(40);

    // Tätigkeit als prominente Überschrift
    if (d.activity) {
      this.pdf.setTextColor(TEAL_R, TEAL_G, TEAL_B);
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.setFontSize(14);
      this.pdf.text(formatActivityLabel(d.activity), this.margin, this.currentY);
      this.pdf.setDrawColor(ORANGE_R, ORANGE_G, ORANGE_B);
      this.pdf.setLineWidth(0.8);
      this.pdf.line(this.margin, this.currentY + 1.6, this.margin + 28, this.currentY + 1.6);
      this.currentY += 6.5;
    }

    // Schlüssel-Wert-Block, kompakt
    const rows: Array<[string, string]> = [];
    if (hasObjekt) rows.push(['Objektbeschaffenheit', capitalizeList(d.objektbeschaffenheit).join(', ')]);
    if (hasMassn) rows.push(['Maßnahmen', capitalizeList(d.massnahmen).join(', ')]);
    if (hasGeschoss) rows.push(['Geschosszahl', String(d.geschosszahl)]);
    if (hasHoehe) rows.push(['Höhe', `${formatDecimal(d.hoehe as number)} m`]);
    if (hasMenge) {
      const einheit = d.mengeneinheit ? ` ${d.mengeneinheit}` : '';
      rows.push(['Mengenausmaß', `${formatDecimal(d.menge as number)}${einheit}`]);
    }

    if (rows.length > 0) {
      const labelW = 38;
      const valueW = this.contentWidth - labelW - 2;
      this.pdf.setFontSize(9);
      rows.forEach(([label, value]) => {
        const wrapped = this.pdf.splitTextToSize(value, valueW) as string[];
        const blockH = Math.max(4.6, wrapped.length * 4.4) + 0.4;
        this.checkPageBreak(blockH + 2);
        this.pdf.setTextColor(100, 110, 110);
        this.pdf.setFont('helvetica', 'normal');
        this.pdf.text(label + ':', this.margin, this.currentY);
        this.pdf.setTextColor(20, 20, 20);
        this.pdf.setFont('helvetica', 'bold');
        wrapped.forEach((wl, idx) => {
          this.pdf.text(wl, this.margin + labelW, this.currentY + idx * 4.4);
        });
        this.currentY += blockH;
      });
    }

    // Shared formatting for AI-generated and pasted project descriptions.
    if (hasDescription) {
      // Dezente Abtrennung zu den Schlüssel/Wert-Zeilen oben.
      if (rows.length > 0 || d.activity) this.currentY += 2;
      // Optionaler zusätzlicher Abstand (Layout-Panel „Abstand vor Leistungsbeschreibung").
      if (this.layout.descriptionTopSpacing > 0) this.currentY += this.layout.descriptionTopSpacing;

      this.checkPageBreak(12);
      this.pdf.setFontSize(9);
      this.pdf.setTextColor(100, 110, 110);
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.text('Leistungsbeschreibung:', this.margin, this.currentY);
      this.currentY += 4.4;

      this.currentY = renderPdfDescription({
        pdf: this.pdf, text: descriptionText, x: this.margin, width: this.contentWidth, y: this.currentY,
        checkPageBreak: (y, height) => {
          this.currentY = y;
          this.checkPageBreak(height);
          return this.currentY;
        },
      });
    }

    this.currentY += 3;
  }

  private addSectionHeader(title: string) {
    this.checkPageBreak(14);
    this.pdf.setFillColor(TEAL_R, TEAL_G, TEAL_B);
    this.pdf.rect(this.margin, this.currentY, this.contentWidth, 7.5, 'F');
    this.pdf.setTextColor(255, 255, 255);
    this.pdf.setFontSize(9);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text(title.toUpperCase(), this.margin + 4, this.currentY + 5.2);
    this.currentY += 10;
  }

  /** Invoice front sheet; the detailed positions and photos remain separate attachments. */
  private invoiceBlock(data: QuoteData, id: InvoiceBlockId, render: () => void) {
    const offset = data.invoiceInfo?.coverPage?.layout?.[id];
    const oldMargin = this.margin;
    const oldWidth = this.contentWidth;
    const page = this.pdf.getNumberOfPages();
    const startY = this.currentY;
    this.margin = Math.min(this.pageWidth - 75, Math.max(5, oldMargin + (offset?.x ?? 0)));
    this.contentWidth = this.pageWidth - oldMargin - this.margin;
    this.currentY = Math.max(35, startY + (offset?.y ?? 0));
    const shiftY = this.currentY - startY;
    this.coverBounds?.activate(id);
    try {
      render();
    } finally {
      this.coverBounds?.activate();
      if (page === this.pdf.getNumberOfPages()) this.currentY -= shiftY;
      this.margin = oldMargin;
      this.contentWidth = oldWidth;
    }
  }

  private addInvoiceCover(data: QuoteData) {
    const info = data.invoiceInfo;
    const cover = info?.coverPage;
    const lineHeight = 4.2;
    const paragraphGap = 1;
    const paragraph = (text: string, bold = false, size = 9, footerClearance = 34) => {
      if (!text.trim()) return;
      this.pdf.setFont("helvetica", bold ? "bold" : "normal");
      this.pdf.setFontSize(size);
      this.pdf.setTextColor(40, 40, 40);
      const lines = this.pdf.splitTextToSize(text, this.contentWidth) as string[];
      for (const line of lines) {
        if (this.currentY + 5 > this.pageHeight - footerClearance) {
          this.pdf.addPage();
          this.currentY = this.margin;
        }
        this.pdf.text(line, this.margin, this.currentY);
        this.currentY += lineHeight;
      }
      this.currentY += paragraphGap;
    };
    const references = [
      cover?.orderNumber ? `Auftrag / Bestellnummer: ${cover.orderNumber}` : "",
      cover?.projectReference ? `Bauvorhaben / Referenz: ${cover.projectReference}` : "",
    ].filter(Boolean);
    this.invoiceBlock(data, "contact", () => paragraph(cover?.contactPerson ? `Bearbeiter: ${cover.contactPerson}` : "", false, 8));
    this.invoiceBlock(data, "identifiers", () => paragraph([
      info?.sellerUid ? `Eigene UID: ${info.sellerUid}` : "",
      cover?.sellerDgNumber ? `Eigene DG-Nr.: ${cover.sellerDgNumber}` : "",
      info?.customerUid ? `Kunden-UID: ${info.customerUid}` : "",
    ].filter(Boolean).join("\n"), false, 8));
    this.invoiceBlock(data, "references", () => paragraph(references.join("\n"), false, 8));
    this.invoiceBlock(data, "intro", () => paragraph(cover?.introText || "Abrechnung der ausgeführten Leistungen gemäß nachfolgender Aufstellung."));
    this.invoiceBlock(data, "servicePeriod", () => paragraph(`Leistungszeitraum: ${info?.servicePeriod || "—"}`));
    this.invoiceBlock(data, "summary", () => {
    this.checkPageBreak(66);
    this.addSectionHeader("Rechnungsübersicht");
    const y = this.currentY;
    const qtyX = this.margin + this.contentWidth * 0.57;
    const epX = this.margin + this.contentWidth * 0.79;
    const totalX = this.margin + this.contentWidth - 2;
    this.pdf.setFillColor(235, 244, 244);
    this.pdf.rect(this.margin, y, this.contentWidth, 8, "F");
    // Section headings use white text; reset it for the pale table and totals.
    this.pdf.setTextColor(40, 40, 40);
    this.pdf.setFont("helvetica", "bold");
    this.pdf.setFontSize(8);
    this.pdf.text("Bezeichnung", this.margin + 2, y + 5);
    this.pdf.text("Menge / ME", qtyX, y + 5, { align: "right" });
    this.pdf.text("Einzelpreis netto", epX, y + 5, { align: "right" });
    this.pdf.text("Gesamt netto", totalX, y + 5, { align: "right" });
    this.pdf.setFont("helvetica", "normal");
    this.pdf.text("Summe lt. Aufstellung", this.margin + 2, y + 14);
    this.pdf.text("1,00 psch.", qtyX, y + 14, { align: "right" });
    this.pdf.text(formatAmount(data.subtotal), epX, y + 14, { align: "right" });
    this.pdf.text(formatAmount(data.subtotal), totalX, y + 14, { align: "right" });
    this.pdf.setDrawColor(200, 215, 215);
    this.pdf.line(this.margin, y + 19, this.margin + this.contentWidth, y + 19);
    const sums = [
      ["Zwischensumme netto", data.subtotal],
      [`Umsatzsteuer (${formatDecimal(this.taxRate, 2, { minFractionDigits: 0 })} %)`, data.tax],
      ["Gesamtsumme brutto", data.total],
    ] as const;
    sums.forEach(([label, amount], index) => {
      this.pdf.setFont("helvetica", index === 2 ? "bold" : "normal");
      this.pdf.setFontSize(index === 2 ? 10 : 8.5);
      this.pdf.text(label, this.margin + this.contentWidth * 0.53, y + 26 + index * 7);
      this.pdf.text(formatAmount(amount), totalX, y + 26 + index * 7, { align: "right" });
    });
    this.currentY = y + 49;
    paragraph("Die detaillierte Leistungsaufstellung folgt auf den nächsten Seiten.", false, 8);
    });
    this.invoiceBlock(data, "paymentTerms", () => {
      if (info?.paymentTerms) paragraph(`Zahlungsbedingungen:\n${info.paymentTerms}`);
    });
    this.invoiceBlock(data, "notes", () => {
      if (data.notes) paragraph(`Hinweise:\n${data.notes}`);
    });
    if (cover?.paymentQr?.enabled) {
      const payload = buildEpcPayment(cover.paymentQr, data.total, data.quoteNumber ?? "");
      const qr = QRCode.create(payload, { errorCorrectionLevel: "M" });
      if (qr.version > 13) throw new Error("Die Zahlungsdaten sind für den SEPA-QR-Code zu lang.");
      // Share a footer-adjacent row: bank/company information left, QR right.
      const companyWidth = this.contentWidth - 48;
      this.pdf.setFont("helvetica", "normal");
      this.pdf.setFontSize(8);
      const bankText = [
        cover.paymentQr.recipient.trim(),
        `IBAN: ${cover.paymentQr.iban.replace(/\s/g, "").toUpperCase()}`,
        cover.paymentQr.bic.trim() ? `BIC: ${cover.paymentQr.bic.replace(/\s/g, "").toUpperCase()}` : "",
      ].filter(Boolean).join("\n");
      const companyText = [cover.additionalCompanyInfo, bankText].filter(Boolean).join("\n");
      // Use the same metrics as paragraph(), including the title and paragraph gaps.
      const companyHeight = (this.pdf.splitTextToSize(companyText, companyWidth) as string[]).length * lineHeight
        + lineHeight + paragraphGap * 2;
      const rowHeight = Math.max(46, companyHeight);
      if (this.currentY + rowHeight + 4 > this.pageHeight - 34) {
        this.pdf.addPage();
        this.addHeaderBanner();
        this.coverHeaderAreas.push({ page: this.pdf.getNumberOfPages(), x: 0, y: 0,
          width: this.pageWidth, height: this.currentY - 4.3 });
      }
      const rowY = Math.max(this.currentY + 4, this.pageHeight - 34 - rowHeight);
      this.currentY = rowY;
      const companyStartPage = this.pdf.getNumberOfPages();
      this.invoiceBlock(data, "companyInfo", () => {
        this.contentWidth = companyWidth;
        paragraph("Firmen- und Bankangaben", true, 8);
        paragraph(companyText, false, 8);
      });
      const companyEnd = this.currentY;
      if (this.pdf.getNumberOfPages() !== companyStartPage) {
        this.pdf.addPage();
        this.addHeaderBanner();
        this.coverHeaderAreas.push({ page: this.pdf.getNumberOfPages(), x: 0, y: 0,
          width: this.pageWidth, height: this.currentY - 4.3 });
      } else this.currentY = rowY;
      const qrLeft = this.contentWidth - 40;
      this.invoiceBlock(data, "paymentQr", () => {
        const x = this.margin + qrLeft;
        const y = this.currentY;
        const size = 40;
        const quietZone = 4;
        const moduleSize = size / (qr.modules.size + quietZone * 2);
        // Vector modules stay sharp in print; include the required white quiet zone.
        this.pdf.setFillColor(255, 255, 255);
        this.pdf.rect(x, y, size, size, "F");
        // The outer rectangle already includes the quiet zone in layout checks.
        this.coverBounds?.activate();
        this.pdf.setFillColor(0, 0, 0);
        for (let row = 0; row < qr.modules.size; row++) {
          for (let col = 0; col < qr.modules.size; col++) {
            if (qr.modules.get(row, col)) this.pdf.rect(
              x + (col + quietZone) * moduleSize, y + (row + quietZone) * moduleSize,
              moduleSize, moduleSize, "F",
            );
          }
        }
        this.coverBounds?.activate("paymentQr");
        this.pdf.setFont("helvetica", "normal");
        this.pdf.setFontSize(7);
        this.pdf.setTextColor(40, 40, 40);
        this.pdf.text("Per Banking-App bezahlen", x, y + 43);
        this.currentY = y + 46;
      });
      this.currentY = Math.max(companyEnd, this.currentY);
      return;
    }
    if (cover?.additionalCompanyInfo) {
      // Anchor short company blocks just above the footer, without covering prior content.
      this.pdf.setFont("helvetica", "normal");
      this.pdf.setFontSize(8);
      const height = (this.pdf.splitTextToSize(cover.additionalCompanyInfo, this.contentWidth) as string[]).length * lineHeight
        + lineHeight + paragraphGap * 2;
      if (height < this.pageHeight - 75) {
        this.checkPageBreak(height + 4);
        this.currentY = Math.max(this.currentY + 4, this.pageHeight - 31 - height);
      }
      this.invoiceBlock(data, "companyInfo", () => {
        paragraph("Zusätzliche Firmen- und Bankangaben", true, 8, 30);
        paragraph(cover.additionalCompanyInfo!, false, 8, 30);
      });
    }
  }

  private addOfferPartDescription(text?: string) {
    if (!text?.trim()) return;
    this.pdf.setFont("helvetica", "normal");
    this.pdf.setFontSize(8);
    this.pdf.setTextColor(40, 60, 60);
    const lines = this.pdf.splitTextToSize(text, this.contentWidth) as string[];
    for (const line of lines) {
      this.checkPageBreak(6);
      this.pdf.text(line, this.margin, this.currentY + 4);
      this.currentY += 5;
    }
    this.currentY += 3;
  }

  /** Invoice grouping is presentation only: billable rows and authoritative
   * invoice totals are supplied by invoiceToPDFData, never offer inclusion flags. */
  private addInvoiceParts(data: QuoteData) {
    this.pdf.addPage();
    this.currentY = 0;
    this.addHeaderBanner();
    let printed = 0;
    let positionNumber = 1;
    for (const part of INVOICE_PARTS) {
      const items = data.items.filter((item) => offerPart(item.teil) === part);
      if (!items.length) continue;
      const title = data.invoiceInfo?.partTitles?.[part]?.trim() || DEFAULT_INVOICE_PART_TITLES[part];
      if (printed++ > 0) this.currentY += 8;
      this.pdf.setFont("helvetica", "bold");
      this.pdf.setFontSize(10);
      const titleLines = this.pdf.splitTextToSize(title, this.contentWidth) as string[];
      this.checkPageBreak(28 + titleLines.length * 5);
      this.pdf.setTextColor(TEAL_R, TEAL_G, TEAL_B);
      titleLines.forEach((line) => {
        this.pdf.text(line, this.margin, this.currentY + 4);
        this.currentY += 5;
      });
      this.currentY += 3;
      this.addItemsTable({ ...data, items }, true, positionNumber);
      positionNumber += items.length;
      const subtotal = Math.round((items.reduce((sum, item) => sum + item.total, 0) + Number.EPSILON) * 100) / 100;
      this.pdf.setFont("helvetica", "bold");
      this.pdf.setFontSize(10);
      const summaryLines = this.pdf.splitTextToSize(
        `Zwischensumme netto – ${title}: ${formatAmount(subtotal)} €`, this.contentWidth,
      ) as string[];
      summaryLines.forEach((line) => {
        this.checkPageBreak(6);
        this.pdf.setTextColor(40, 60, 60);
        this.pdf.text(line, this.margin + this.contentWidth, this.currentY + 4, { align: "right" });
        this.currentY += 5;
      });
    }
    this.currentY += 8;
    this.renderTotalsBox(data.subtotal, data.tax, data.total);
  }

  /** Reuse the established position/photo renderers; only offer grouping and
   * its totals are new. Invoices and A-only offers stay on the original path. */
  private async addOfferParts(data: QuoteData) {
    const totals = calculateOfferParts([
      ...data.items, ...(data.perPhoto?.flatMap((photo) => photo.positions) ?? []),
    ], data.offerParts);
    const paragraph = (text: string, bold = false, fontSize = bold ? 10 : 8, right = false, teal = false) => {
      this.pdf.setFont("helvetica", bold ? "bold" : "normal");
      this.pdf.setFontSize(fontSize);
      this.pdf.setTextColor(teal ? TEAL_R : 40, teal ? TEAL_G : 60, teal ? TEAL_B : 60);
      const lines = this.pdf.splitTextToSize(text, this.contentWidth) as string[];
      for (const line of lines) {
        this.checkPageBreak(6);
        this.pdf.text(line, right ? this.margin + this.contentWidth : this.margin, this.currentY + 4, { align: right ? "right" : "left" });
        this.currentY += 5;
      }
      this.currentY += 3;
    };
    this.pdf.addPage();
    this.currentY = 0;
    this.addHeaderBanner();
    let partIndex = 0;
    for (const part of totals.parts) {
      if (partIndex++ > 0) this.currentY += 8;
      this.checkPageBreak(32);
      paragraph(part.title, true, 10, false, true);
      if (part.description?.trim()) paragraph(part.description);
      if (!part.included) paragraph(OPTIONAL_PART_NOTE);
      const items = data.items.filter((item) => offerPart(item.teil) === part.id);
      if (items.length) this.addItemsTable({ ...data, items }, true);
      const perPhoto = data.perPhoto?.map((photo) => {
        const positions = photo.positions.filter((position) => offerPart(position.teil) === part.id);
        return {
          ...photo, photoLabel: `${part.title} · ${photo.photoLabel}`, positions,
          subtotal: positions.reduce((sum, position) => position.variant ? sum : sum + position.total, 0),
        };
      }).filter((photo) => photo.positions.length > 0);
      if (perPhoto?.length) await this.addPerPhotoBreakdown({ ...data, items, perPhoto }, true);
      paragraph(`Zwischensumme netto – ${part.title}: ${formatAmount(part.subtotal)} €`, true, 10, true);
    }
    const variantSubtotal = totals.parts.flatMap((part) => part.positions)
      .reduce((sum, position) => position.variant ? sum + position.total : sum, 0);
    this.addOfferPartsOverview(totals, variantSubtotal);
    this.renderTotalsBox(totals.subtotal, totals.tax, totals.total, variantSubtotal, "Gesamtsumme netto:");
  }

  private addOfferPartsOverview(totals: Parameters<typeof offerPartsOverview>[0], variantSubtotal: number) {
    const overview = offerPartsOverview(totals);
    const width = this.contentWidth;
    const x = this.margin;
    const edges = [0, width / 2, width * 2 / 3, width * 5 / 6, width];
    this.pdf.setFont("helvetica", "normal");
    this.pdf.setFontSize(10);
    const rows = overview.rows.map((row) => {
      const title = `${row.title}${row.included ? "" : " (optional)"}`;
      return { ...row, lines: [title.replace(/\s+/g, " ")], height: 8 };
    });
    this.pdf.setFontSize(7);
    const noteLines = overview.roundingNote ? this.pdf.splitTextToSize(overview.roundingNote, width) as string[] : [];
    const noteHeight = noteLines.length ? noteLines.length * 3.5 + 4 : 0;
    this.checkPageBreak(noteHeight + 9 + 8 + rows.reduce((sum, row) => sum + row.height, 0) + 10 + (variantSubtotal > 0 ? 43 : 36));
    if (noteLines.length) {
      this.pdf.setFont("helvetica", "normal"); this.pdf.setFontSize(7); this.pdf.setTextColor(130, 90, 20);
      for (const line of noteLines) { this.pdf.text(line, x, this.currentY + 3); this.currentY += 3.5; }
      this.currentY += 4;
    }
    this.pdf.setFont("helvetica", "bold"); this.pdf.setFontSize(10); this.pdf.setTextColor(40, 60, 60);
    this.pdf.text("Übersicht der Angebotsteile", x, this.currentY + 4);
    this.currentY += 9;
    const drawRow = (titleLines: string[], amounts: string[], height: number, bold = false, optional = false, fill = false) => {
      if (fill) { this.pdf.setFillColor(MINT_R, MINT_G, MINT_B); this.pdf.rect(x, this.currentY, width, height, "F"); }
      this.pdf.setFont("helvetica", bold ? "bold" : "normal");
      this.pdf.setFontSize(10);
      this.pdf.setTextColor(optional ? 130 : 40, optional ? 130 : 60, optional ? 130 : 60);
      this.pdf.saveGraphicsState();
      titleLines.forEach((line) => this.pdf.text(line, x + 4, this.currentY + 5,
        { horizontalScale: Math.min(1, (edges[1] - 8) / Math.max(1, this.pdf.getTextWidth(line))) }));
      amounts.forEach((value, index) => {
        const textWidth = this.pdf.getTextWidth(value);
        const scale = Math.min(1, (edges[index + 2] - edges[index + 1] - 8) / Math.max(1, textWidth));
        const right = x + edges[index + 2] - 4;
        // jsPDF's right alignment uses the unscaled width. Anchor oversized
        // one-line values explicitly so their visible right edge stays aligned.
        this.pdf.text(value, scale < 1 ? right - textWidth * scale : right, this.currentY + 5,
          { align: scale < 1 ? "left" : "right", horizontalScale: scale });
      });
      this.pdf.restoreGraphicsState();
      this.currentY += height;
      this.pdf.setDrawColor(210, 220, 220); this.pdf.setLineWidth(0.15);
      this.pdf.line(x, this.currentY, x + width, this.currentY);
    };
    drawRow(["Angebotsteil"], ["Netto", "MwSt. 20 %", "Brutto"], 8, false, false, true);
    const money = (value: number) => `${formatAmount(value / 100)} €`;
    rows.forEach((row) => drawRow(row.lines, [money(row.net), money(row.tax), money(row.gross)], row.height, false, !row.included));
    drawRow(["Summe"], [money(overview.sum.net), money(overview.sum.tax), money(overview.sum.gross)], 8, true, false, true);
    this.currentY += 2;
  }

  /** Full grouped-offer position text, including rows spanning multiple pages. */
  private addGroupedPositionRow(item: QuoteData["items"][number], index: number, number?: number) {
    const desc = this.col("desc");
    this.pdf.setFont("helvetica", item.variant ? "italic" : "normal");
    this.pdf.setFontSize(8.5);
    const lines = (this.pdf.splitTextToSize((item.description || "—") + (item.variant ? "  (Variante)" : ""), desc.w - 2) as string[])
      .map((text) => ({ text, size: 8.5, style: item.variant ? "italic" : "normal", source: false }));
    if (item.sourceLabel) {
      this.pdf.setFont("helvetica", "normal"); this.pdf.setFontSize(7);
      lines.push(...(this.pdf.splitTextToSize(item.sourceLabel, desc.w - 2) as string[])
        .map((text) => ({ text, size: 7, style: "normal", source: true })));
    }
    if (item.beschreibung?.trim()) {
      this.pdf.setFont("helvetica", "italic"); this.pdf.setFontSize(7.5);
      lines.push(...(this.pdf.splitTextToSize(item.beschreibung, desc.w - 2) as string[])
        .map((text) => ({ text, size: 7.5, style: "italic", source: false })));
    }
    this.checkPageBreak(Math.min(lines.length * 4.4 + 2, this.pageHeight - this.margin - 34));
    lines.forEach((line, lineIndex) => {
      this.checkPageBreak(6.4);
      if (item.variant || index % 2 === 1) {
        this.pdf.setFillColor(item.variant ? 254 : ALT_R, item.variant ? 247 : ALT_G, item.variant ? 224 : ALT_B);
        this.pdf.rect(this.margin, this.currentY, this.contentWidth, 4.4, "F");
      }
      const y = this.currentY + 3.5;
      this.pdf.setFont("helvetica", line.style); this.pdf.setFontSize(line.size);
      this.pdf.setTextColor(line.source ? 80 : 30, line.source ? 130 : 30, line.source ? 110 : 30);
      this.pdf.text(line.text, desc.x, y);
      if (lineIndex === 0) {
        this.pdf.setFont("helvetica", "normal"); this.pdf.setFontSize(8.5); this.pdf.setTextColor(50, 50, 50);
        if (number !== undefined) this.pdf.text(String(number), this.col("pos").x + 1, y);
        for (const [column, value] of [
          ["qty", formatDecimal(item.quantity, 6, { minFractionDigits: 0 })], ["ep", formatAmount(item.unitPrice)],
          ["total", item.variant ? `(${formatAmount(item.total)})` : formatAmount(item.total)],
        ] as const) {
          if (column === "total") this.pdf.setFont("helvetica", item.variant ? "italic" : "bold");
          const cell = this.col(column);
          this.pdf.text(value, cell.x + cell.w, y, { align: "right" });
        }
        this.pdf.setFont("helvetica", "normal");
        this.pdf.text(item.unit, this.col("unit").x + 1, y);
      }
      this.currentY += 4.4;
    });
    this.currentY += lines.length === 1 ? 2.6 : 2;
    this.pdf.setDrawColor(210, 215, 215); this.pdf.setLineWidth(0.15);
    this.pdf.line(this.margin, this.currentY, this.margin + this.contentWidth, this.currentY);
  }

  private addItemsTable(data: QuoteData, suppressTotals = false, invoiceStartNumber = 1) {
    this.addSectionHeader('Leistungspositionen');

    const rowH = 7;
    const tableY = this.currentY;

    this.pdf.setFillColor(TEAL_R, TEAL_G, TEAL_B);
    this.pdf.rect(this.margin, tableY, this.contentWidth, rowH, 'F');

    this.pdf.setTextColor(255, 255, 255);
    this.pdf.setFontSize(8.5);
    this.pdf.setFont('helvetica', 'bold');

    const posC = this.col('pos');
    const descC = this.col('desc');
    const qtyC = this.col('qty');
    const unitC = this.col('unit');
    const epC = this.col('ep');
    const totalC = this.col('total');

    const headerY = tableY + 4.8;
    this.pdf.text('Pos.', posC.x + 1, headerY);
    this.pdf.text('Beschreibung', descC.x, headerY);
    this.pdf.text('Menge', qtyC.x + qtyC.w, headerY, { align: 'right' });
    this.pdf.text('Einheit', unitC.x + 1, headerY);
    this.pdf.text('EP (€)', epC.x + epC.w, headerY, { align: 'right' });
    this.pdf.text('Gesamt (€)', totalC.x + totalC.w, headerY, { align: 'right' });

    this.currentY = tableY + rowH;

    if (this.documentType === "invoice") this.addInvoiceItemRows(data, invoiceStartNumber);
    else data.items.forEach((item, index) => {
      if (suppressTotals) { this.addGroupedPositionRow(item, index, index + 1); return; }
      const hasSource = !!item.sourceLabel;
      const beschreibungText = (item.beschreibung ?? '').trim();
      const hasBeschreibung = beschreibungText.length > 0;
      const beschreibungWidth = this.contentWidth - (descC.x - this.margin) - 2;
      const beschreibungLineH = 3.4;
      const beschreibungLines = hasBeschreibung
        ? (this.pdf.splitTextToSize(beschreibungText, beschreibungWidth) as string[])
        : [];
      const beschreibungBlockH = hasBeschreibung
        ? beschreibungLines.length * beschreibungLineH + 2
        : 0;
      const itemH = (hasSource ? rowH + 4 : rowH) + beschreibungBlockH;
      this.checkPageBreak(itemH + 2);

      if (index % 2 === 1) {
        this.pdf.setFillColor(ALT_R, ALT_G, ALT_B);
        this.pdf.rect(this.margin, this.currentY, this.contentWidth, itemH, 'F');
      }

      const cellY = this.currentY + 4.8;

      this.pdf.setTextColor(90, 90, 90);
      this.pdf.setFontSize(8.5);
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.text(String(index + 1), posC.x + 1, cellY);

      const descSuffix = item.variant ? '  (Variante)' : '';
      const descLines = this.pdf.splitTextToSize((item.description || '—') + descSuffix, descC.w - 2);
      this.pdf.setTextColor(item.variant ? 150 : 20, item.variant ? 110 : 20, item.variant ? 30 : 20);
      this.pdf.text(descLines[0] + (descLines.length > 1 ? ' …' : ''), descC.x, cellY);

      if (hasSource) {
        const sourceTrunc = item.sourceLabel!.length > 48 ? item.sourceLabel!.slice(0, 48) + '…' : item.sourceLabel!;
        this.pdf.setFontSize(6.5);
        this.pdf.setTextColor(80, 130, 110);
        this.pdf.text(`↳ ${sourceTrunc}`, descC.x + 1, cellY + 3.8);
      }

      this.pdf.setFontSize(8.5);
      this.pdf.setTextColor(50, 50, 50);
      this.pdf.text(
        formatDecimal(item.quantity, 6, { minFractionDigits: 0 }),
        qtyC.x + qtyC.w, cellY, { align: 'right' }
      );
      this.pdf.text(item.unit, unitC.x + 1, cellY);
      this.pdf.text(formatAmount(item.unitPrice), epC.x + epC.w, cellY, { align: 'right' });

      if (item.variant) {
        this.pdf.setTextColor(150, 110, 30);
        this.pdf.setFont('helvetica', 'italic');
      } else {
        this.pdf.setTextColor(20, 20, 20);
        this.pdf.setFont('helvetica', 'bold');
      }
      this.pdf.text(
        item.variant ? `(${formatAmount(item.total)})` : formatAmount(item.total),
        totalC.x + totalC.w,
        cellY,
        { align: 'right' },
      );

      if (hasBeschreibung) {
        const baseY = this.currentY + (hasSource ? rowH + 4 : rowH);
        this.pdf.setFont('helvetica', 'italic');
        this.pdf.setFontSize(7.5);
        this.pdf.setTextColor(70, 80, 80);
        beschreibungLines.forEach((ln, i) => {
          this.pdf.text(ln, descC.x, baseY + i * beschreibungLineH + 2.4);
        });
      }

      this.pdf.setDrawColor(210, 215, 215);
      this.pdf.setLineWidth(0.15);
      this.pdf.line(this.margin, this.currentY + itemH, this.margin + this.contentWidth, this.currentY + itemH);

      this.currentY += itemH;
    });

    this.currentY += 8;

    // Wenn eine Foto-Aufschlüsselung vorhanden ist, wird der finale
    // Summenblock erst nach den Foto-Blöcken gerendert (Leistungspositionen
    // + Foto-Zwischensummen = Netto). Hier deshalb überspringen.
    if (suppressTotals || (data.perPhoto && data.perPhoto.length > 0)) {
      return;
    }

    const variantSubtotal = data.items.reduce(
      (sum, item) => item.variant ? sum + item.total : sum,
      0,
    );
    this.renderTotalsBox(data.subtotal, data.tax, data.total, variantSubtotal);
  }

  /** Invoice text is user-editable: print every line without a generated prefix or truncation. */
  private addInvoiceItemRows(data: QuoteData, startNumber = 1) {
    const posC = this.col('pos');
    const descC = this.col('desc');
    const qtyC = this.col('qty');
    const unitC = this.col('unit');
    const epC = this.col('ep');
    const totalC = this.col('total');
    data.items.forEach((item, index) => {
      this.pdf.setFont("helvetica", "normal");
      this.pdf.setFontSize(8.5);
      const descriptionLines = this.pdf.splitTextToSize(item.description || "—", descC.w - 2) as string[];
      this.pdf.setFontSize(7);
      const sourceLines = item.sourceLabel?.trim()
        ? this.pdf.splitTextToSize(item.sourceLabel, descC.w - 3) as string[] : [];
      const lines = [
        ...descriptionLines.map((text) => ({ text, source: false })),
        ...sourceLines.map((text) => ({ text, source: true })),
      ];
      lines.forEach((line, lineIndex) => {
        this.checkPageBreak(6);
        if (index % 2 === 1) {
          this.pdf.setFillColor(ALT_R, ALT_G, ALT_B);
          this.pdf.rect(this.margin, this.currentY, this.contentWidth, 5.5, "F");
        }
        const y = this.currentY + 4;
        this.pdf.setFont("helvetica", "normal");
        this.pdf.setFontSize(line.source ? 7 : 8.5);
        this.pdf.setTextColor(line.source ? 70 : 20, line.source ? 95 : 20, line.source ? 95 : 20);
        this.pdf.text(line.text, descC.x + (line.source ? 1 : 0), y);
        if (lineIndex === 0) {
          this.pdf.setFontSize(8.5);
          this.pdf.setTextColor(40, 40, 40);
          this.pdf.text(String(startNumber + index), posC.x + 1, y);
          this.pdf.text(formatDecimal(item.quantity, 6, { minFractionDigits: 0 }), qtyC.x + qtyC.w, y, { align: "right" });
          this.pdf.text(item.unit, unitC.x + 1, y);
          this.pdf.text(formatAmount(item.unitPrice), epC.x + epC.w, y, { align: "right" });
          this.pdf.setFont("helvetica", "bold");
          this.pdf.text(formatAmount(item.total), totalC.x + totalC.w, y, { align: "right" });
        }
        this.currentY += 5.5;
      });
      this.currentY += 1.5;
      this.pdf.setDrawColor(210, 215, 215);
      this.pdf.setLineWidth(0.15);
      this.pdf.line(this.margin, this.currentY, this.margin + this.contentWidth, this.currentY);
    });
  }

  private renderTotalsBox(subtotal: number, tax: number, total: number, variantSubtotal = 0, netLabel = 'Nettobetrag:') {
    this.checkPageBreak(variantSubtotal > 0 ? 43 : 36);

    const sumW = this.documentType === "quote" && netLabel === "Gesamtsumme netto:" ? this.contentWidth : 88;
    const sumX = this.margin + this.contentWidth - sumW;
    const sumY = this.currentY;

    this.pdf.setFillColor(250, 252, 252);
    this.pdf.setDrawColor(MINT_R, MINT_G, MINT_B);
    this.pdf.setLineWidth(0.4);
    const boxH = variantSubtotal > 0 ? 29 : 22;
    this.pdf.roundedRect(sumX, sumY, sumW, boxH, 2, 2, 'FD');

    this.pdf.setTextColor(70, 80, 80);
    this.pdf.setFontSize(9);
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.text(netLabel, sumX + 4, sumY + 7);
    this.pdf.text(`${formatAmount(subtotal)} €`, sumX + sumW - 4, sumY + 7, { align: 'right' });

    if (variantSubtotal > 0) {
      this.pdf.setTextColor(150, 110, 30);
      this.pdf.setFont('helvetica', 'italic');
      this.pdf.text('Summe der Variantenleistungen:', sumX + 4, sumY + 13);
      this.pdf.text(`(${formatAmount(variantSubtotal)} €)`, sumX + sumW - 4, sumY + 13, { align: 'right' });
      this.pdf.setTextColor(70, 80, 80);
      this.pdf.setFont('helvetica', 'normal');
    }

    const offsetY = variantSubtotal > 0 ? 7 : 0;
    this.pdf.setDrawColor(200, 210, 210);
    this.pdf.setLineWidth(0.2);
    this.pdf.line(sumX + 4, sumY + 10 + offsetY, sumX + sumW - 4, sumY + 10 + offsetY);

    this.pdf.text(`MwSt. (${formatDecimal(this.taxRate, 2, { minFractionDigits: 0 })} %):`, sumX + 4, sumY + 16 + offsetY);
    this.pdf.text(`${formatAmount(tax)} €`, sumX + sumW - 4, sumY + 16 + offsetY, { align: 'right' });

    this.currentY = sumY + boxH + 2;

    this.pdf.setFillColor(TEAL_R, TEAL_G, TEAL_B);
    this.pdf.roundedRect(sumX, this.currentY, sumW, 11, 2, 2, 'F');
    this.pdf.setTextColor(255, 255, 255);
    this.pdf.setFontSize(10.5);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text('Gesamtbetrag', sumX + 4, this.currentY + 7.2);
    this.pdf.text(`${formatAmount(total)} €`, sumX + sumW - 4, this.currentY + 7.2, { align: 'right' });

    this.currentY += 11 + 12;
  }

  private async addPerPhotoBreakdown(data: QuoteData, suppressTotals = false) {
    const perPhoto = data.perPhoto!;
    this.addSectionHeader('Aufschlüsselung pro Bild');

    // Kompakte Übersicht aller Foto-Zwischensummen vorab als schneller Überblick.
    this.renderPhotoSubtotalsOverview(perPhoto);

    // Detail-Foto-Blöcke beginnen IMMER auf einer frischen Seite (im
    // Standardfall Seite 3). Die kompakte Übersicht oben bleibt damit
    // direkt unter den Leistungspositionen auf Seite 2 sichtbar.
    this.pdf.addPage();
    this.currentY = this.margin;

    const descC = this.col('desc');
    const qtyC = this.col('qty');
    const unitC = this.col('unit');
    const epC = this.col('ep');
    const totalC = this.col('total');

    const rowH = 5.6;
    const THUMB_SIZE = 16; // mm — quadratischer Bildbereich im Header
    const THUMB_PAD = 2;   // Padding um das Thumbnail innerhalb des Header-Bands
    let totalAll = 0;

    // Vorschaubilder vorab komprimieren (parallel) und Maße bestimmen, damit die
    // forEach-Schleife synchron rendern kann und die Seitenumbruch-Berechnung
    // verlässlich ist.
    type ThumbInfo = { src: string; drawW: number; drawH: number } | null;
    const thumbnails: ThumbInfo[] = await Promise.all(
      perPhoto.map(async (e) => {
        if (!e.preview) return null;
        try {
          const src = await this.compressImage(e.preview, 256, 0.75);
          const dims = await this.getImageDimensions(src);
          let drawW = THUMB_SIZE;
          let drawH = THUMB_SIZE;
          if (dims && dims.width > 0 && dims.height > 0) {
            const ratio = dims.width / dims.height;
            if (ratio >= 1) drawH = THUMB_SIZE / ratio;
            else drawW = THUMB_SIZE * ratio;
          }
          return { src, drawW, drawH };
        } catch {
          return null;
        }
      })
    );

    perPhoto.forEach((entry, entryIdx) => {
      totalAll += entry.subtotal;
      const thumb = thumbnails[entryIdx];
      const headerH = thumb ? THUMB_SIZE + THUMB_PAD * 2 : 7;
      const blockH = headerH + 2 + entry.positions.length * rowH + 8;
      this.checkPageBreak(blockH);

      this.pdf.setFillColor(MINT_R, MINT_G, MINT_B);
      this.pdf.rect(this.margin, this.currentY, this.contentWidth, headerH, 'F');

      let textLeftX = this.margin + 3;
      if (thumb) {
        const ix = this.margin + THUMB_PAD + (THUMB_SIZE - thumb.drawW) / 2;
        const iy = this.currentY + THUMB_PAD + (THUMB_SIZE - thumb.drawH) / 2;
        try {
          this.pdf.addImage(thumb.src, getImageFormat(thumb.src), ix, iy, thumb.drawW, thumb.drawH);
        } catch { /* ignore */ }
        textLeftX = this.margin + THUMB_PAD + THUMB_SIZE + 3;
      }

      this.pdf.setTextColor(20, 50, 50);
      this.pdf.setFontSize(8.5);
      this.pdf.setFont('helvetica', 'bold');
      const headLeft = entry.fileName
        ? `${entry.photoLabel} — ${entry.fileName}`
        : entry.photoLabel;
      // Reserviere Platz rechts für die Aufmaß-Anzeige, damit der Header-Text
      // nicht überlappt. Bei vorhandenem Thumbnail darf der Text über 2 Zeilen
      // umgebrochen werden (genug vertikaler Raum), sonst hart auf 1 Zeile
      // gekürzt, da das schmale 7mm-Band keine 2 Zeilen aufnimmt.
      const hasAmount = entry.amount !== undefined && entry.amount > 0;
      const reservedRight = hasAmount ? 34 : 3;
      const availableTextW = Math.max(20, (this.margin + this.contentWidth - reservedRight) - textLeftX);
      const maxLines = thumb ? 2 : 1;
      const splitAll = this.pdf.splitTextToSize(headLeft, availableTextW) as string[];
      const wrapped = splitAll.slice(0, maxLines);
      if (splitAll.length > maxLines && wrapped.length > 0) {
        const last = wrapped[wrapped.length - 1];
        wrapped[wrapped.length - 1] = last.replace(/\s*\S{0,2}$/, '') + '…';
      }
      const lineH = 4.2; // mm bei 8.5pt
      const totalTextH = wrapped.length * lineH;
      const firstLineY = this.currentY + (headerH - totalTextH) / 2 + (lineH - 1.2);
      wrapped.forEach((ln, lnIdx) => {
        this.pdf.text(ln, textLeftX, firstLineY + lnIdx * lineH);
      });
      if (hasAmount) {
        const u = entry.unit ?? 'm²';
        const aufmassY = this.currentY + headerH / 2 + 1.5;
        this.pdf.text(
          `Aufmaß: ${formatDecimal(entry.amount as number, 2)} ${u}`,
          this.margin + this.contentWidth - 3, aufmassY,
          { align: 'right' }
        );
      }
      this.currentY += headerH;

      this.pdf.setTextColor(110, 115, 115);
      this.pdf.setFontSize(7);
      this.pdf.setFont('helvetica', 'normal');
      const headerY = this.currentY + 3.6;
      this.pdf.text('Leistung', descC.x, headerY);
      this.pdf.text('Menge', qtyC.x + qtyC.w, headerY, { align: 'right' });
      this.pdf.text('Einheit', unitC.x + 1, headerY);
      this.pdf.text('EP (€)', epC.x + epC.w, headerY, { align: 'right' });
      this.pdf.text('Gesamt (€)', totalC.x + totalC.w, headerY, { align: 'right' });
      this.currentY += 5;

      const nameLineH = 4.0; // mm per wrapped line at 8pt
      entry.positions.forEach((pos, idx) => {
        if (suppressTotals) {
          this.addGroupedPositionRow({
            description: pos.beschreibung?.trim() || pos.name, quantity: pos.qty, unit: pos.unit,
            unitPrice: pos.ep, total: pos.total, variant: pos.variant,
          }, idx);
          return;
        }
        this.pdf.setFontSize(8);
        const leistungstext = pos.beschreibung?.trim() || pos.name;
        const nameText = pos.variant ? leistungstext + '  (Variante)' : leistungstext;
        const nameLines = this.pdf.splitTextToSize(nameText, descC.w - 2) as string[];
        const beschreibungLines: string[] = [];
        const posRowH = Math.max(
          rowH,
          nameLines.length * nameLineH + beschreibungLines.length * 3.4 + (beschreibungLines.length ? 1.5 : 0) + 2.5,
        );
        this.checkPageBreak(posRowH + 2);
        if (pos.variant) {
          this.pdf.setFillColor(254, 247, 224); // sehr helles Amber
          this.pdf.rect(this.margin, this.currentY, this.contentWidth, posRowH, 'F');
        } else if (idx % 2 === 1) {
          this.pdf.setFillColor(ALT_R, ALT_G, ALT_B);
          this.pdf.rect(this.margin, this.currentY, this.contentWidth, posRowH, 'F');
        }
        const cellY = this.currentY + 4;
        // Zahlen-Spalten bei mehrzeiligem Text vertikal zentrieren
        const numY =
          nameLines.length > 1 || beschreibungLines.length > 0
            ? this.currentY + posRowH / 2 + 1.5
            : cellY;
        if (pos.variant) {
          this.pdf.setTextColor(150, 110, 30);
          this.pdf.setFont('helvetica', 'italic');
        } else {
          this.pdf.setTextColor(30, 30, 30);
          this.pdf.setFont('helvetica', 'normal');
        }
        nameLines.forEach((line, li) => {
          this.pdf.text(line, descC.x, cellY + li * nameLineH);
        });
        if (beschreibungLines.length > 0) {
          this.pdf.setFontSize(7);
          this.pdf.setTextColor(100, 105, 105);
          const beschreibungY = cellY + nameLines.length * nameLineH + 0.5;
          beschreibungLines.forEach((line, li) => {
            this.pdf.text(line, descC.x, beschreibungY + li * 3.4);
          });
        }
        this.pdf.setFontSize(8);
        this.pdf.setTextColor(30, 30, 30);
        this.pdf.setFont('helvetica', 'normal');
        this.pdf.text(
          formatDecimal(pos.qty, 6, { minFractionDigits: 0 }),
          qtyC.x + qtyC.w, numY, { align: 'right' }
        );
        this.pdf.text(pos.unit, unitC.x + 1, numY);
        this.pdf.text(formatAmount(pos.ep), epC.x + epC.w, numY, { align: 'right' });
        if (pos.variant) {
          this.pdf.setTextColor(150, 110, 30);
          this.pdf.setFont('helvetica', 'italic');
        } else {
          this.pdf.setFont('helvetica', 'bold');
        }
        this.pdf.text(
          pos.variant ? `(${formatAmount(pos.total)})` : formatAmount(pos.total),
          totalC.x + totalC.w,
          numY,
          { align: 'right' },
        );
        this.currentY += posRowH;
      });

      this.checkPageBreak(8);
      this.pdf.setDrawColor(MINT_R, MINT_G, MINT_B);
      this.pdf.setLineWidth(0.3);
      this.pdf.line(this.margin, this.currentY, this.margin + this.contentWidth, this.currentY);
      this.currentY += 1;
      this.pdf.setTextColor(40, 80, 80);
      this.pdf.setFontSize(8.5);
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.text('Zwischensumme', this.margin + 3, this.currentY + 4.8);
      this.pdf.text(
        `${formatAmount(entry.subtotal)} €`,
        this.margin + this.contentWidth - 3, this.currentY + 4.8,
        { align: 'right' }
      );
      this.currentY += 7;
      const entryVariantSubtotal = entry.positions.reduce(
        (sum, pos) => pos.variant ? sum + pos.total : sum,
        0,
      );
      if (entryVariantSubtotal > 0) {
        this.checkPageBreak(7);
        this.pdf.setTextColor(150, 110, 30);
        this.pdf.setFontSize(8);
        this.pdf.setFont('helvetica', 'italic');
        this.pdf.text('Summe der Variantenleistungen', this.margin + 3, this.currentY + 4.8);
        this.pdf.text(
          `(${formatAmount(entryVariantSubtotal)} €)`,
          this.margin + this.contentWidth - 3,
          this.currentY + 4.8,
          { align: 'right' },
        );
        this.currentY += 7;
      }
      this.currentY += 4;
    });

    if (suppressTotals) return;
    // Finaler Summenblock: Leistungspositionen + Foto-Zwischensummen = Netto.
    // data.subtotal enthält bereits die Summe aus manuellen Items + perPhoto.
    const itemsSubtotal = data.items.reduce(
      (sum, it) => (it.variant ? sum : sum + it.total),
      0
    );
    const variantSubtotal =
      data.items.reduce((sum, it) => it.variant ? sum + it.total : sum, 0) +
      perPhoto.reduce(
        (sum, entry) => sum + entry.positions.reduce(
          (entrySum, pos) => pos.variant ? entrySum + pos.total : entrySum,
          0,
        ),
        0,
      );

    this.checkPageBreak(variantSubtotal > 0 ? 53 : 46);

    const sumW = 100;
    const sumX = this.margin + this.contentWidth - sumW;
    const sumY = this.currentY;

    this.pdf.setFillColor(250, 252, 252);
    this.pdf.setDrawColor(MINT_R, MINT_G, MINT_B);
    this.pdf.setLineWidth(0.4);
    const totalsBoxH = variantSubtotal > 0 ? 35 : 28;
    this.pdf.roundedRect(sumX, sumY, sumW, totalsBoxH, 2, 2, 'FD');

    this.pdf.setTextColor(70, 80, 80);
    this.pdf.setFontSize(9);
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.text('Leistungspositionen:', sumX + 4, sumY + 6);
    this.pdf.text(`${formatAmount(itemsSubtotal)} €`, sumX + sumW - 4, sumY + 6, { align: 'right' });

    this.pdf.text('Foto-Zwischensummen:', sumX + 4, sumY + 12);
    this.pdf.text(`${formatAmount(totalAll)} €`, sumX + sumW - 4, sumY + 12, { align: 'right' });

    let totalsOffsetY = 0;
    if (variantSubtotal > 0) {
      this.pdf.setTextColor(150, 110, 30);
      this.pdf.setFont('helvetica', 'italic');
      this.pdf.text('Summe der Variantenleistungen:', sumX + 4, sumY + 18);
      this.pdf.text(`(${formatAmount(variantSubtotal)} €)`, sumX + sumW - 4, sumY + 18, { align: 'right' });
      totalsOffsetY = 7;
      this.pdf.setTextColor(70, 80, 80);
      this.pdf.setFont('helvetica', 'normal');
    }

    this.pdf.setDrawColor(200, 210, 210);
    this.pdf.setLineWidth(0.2);
    this.pdf.line(sumX + 4, sumY + 15 + totalsOffsetY, sumX + sumW - 4, sumY + 15 + totalsOffsetY);

    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text('Nettobetrag:', sumX + 4, sumY + 21 + totalsOffsetY);
    this.pdf.text(`${formatAmount(data.subtotal)} €`, sumX + sumW - 4, sumY + 21 + totalsOffsetY, { align: 'right' });

    this.pdf.setFont('helvetica', 'normal');
    this.pdf.text(`MwSt. (${formatDecimal(this.taxRate, 2, { minFractionDigits: 0 })} %):`, sumX + 4, sumY + 26 + totalsOffsetY);
    this.pdf.text(`${formatAmount(data.tax)} €`, sumX + sumW - 4, sumY + 26 + totalsOffsetY, { align: 'right' });

    this.currentY = sumY + totalsBoxH + 2;

    this.pdf.setFillColor(TEAL_R, TEAL_G, TEAL_B);
    this.pdf.roundedRect(sumX, this.currentY, sumW, 11, 2, 2, 'F');
    this.pdf.setTextColor(255, 255, 255);
    this.pdf.setFontSize(10.5);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text('Gesamtbetrag', sumX + 4, this.currentY + 7.2);
    this.pdf.text(`${formatAmount(data.total)} €`, sumX + sumW - 4, this.currentY + 7.2, { align: 'right' });

    this.currentY += 11 + 12;
  }

  private renderPhotoSubtotalsOverview(perPhoto: NonNullable<QuoteData['perPhoto']>) {
    const rowH = 5.6;
    const headerH = 7;
    const blockH = headerH + perPhoto.length * rowH + 8;
    this.checkPageBreak(blockH + 4);

    this.pdf.setFillColor(MINT_R, MINT_G, MINT_B);
    this.pdf.rect(this.margin, this.currentY, this.contentWidth, headerH, 'F');
    this.pdf.setTextColor(20, 50, 50);
    this.pdf.setFontSize(8.5);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text('Übersicht Foto-Zwischensummen', this.margin + 3, this.currentY + 4.8);
    this.pdf.text(
      'Zwischensumme',
      this.margin + this.contentWidth - 3,
      this.currentY + 4.8,
      { align: 'right' }
    );
    this.currentY += headerH;

    let totalAll = 0;
    perPhoto.forEach((entry, idx) => {
      this.checkPageBreak(rowH + 2);
      if (idx % 2 === 1) {
        this.pdf.setFillColor(ALT_R, ALT_G, ALT_B);
        this.pdf.rect(this.margin, this.currentY, this.contentWidth, rowH, 'F');
      }
      const cellY = this.currentY + 4;
      this.pdf.setTextColor(30, 30, 30);
      this.pdf.setFontSize(8);
      this.pdf.setFont('helvetica', 'normal');
      const label = entry.fileName
        ? `${entry.photoLabel} — ${entry.fileName}`
        : entry.photoLabel;
      this.pdf.text(this.truncate(label, 80), this.margin + 3, cellY);
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.text(
        `${formatAmount(entry.subtotal)} €`,
        this.margin + this.contentWidth - 3,
        cellY,
        { align: 'right' }
      );
      this.currentY += rowH;
      totalAll += entry.subtotal;
    });

    this.pdf.setDrawColor(MINT_R, MINT_G, MINT_B);
    this.pdf.setLineWidth(0.3);
    this.pdf.line(this.margin, this.currentY, this.margin + this.contentWidth, this.currentY);
    this.currentY += 1;
    this.pdf.setTextColor(40, 80, 80);
    this.pdf.setFontSize(8.5);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text('Gesamt Foto-Zwischensummen', this.margin + 3, this.currentY + 4.8);
    this.pdf.text(
      `${formatAmount(totalAll)} €`,
      this.margin + this.contentWidth - 3,
      this.currentY + 4.8,
      { align: 'right' }
    );
    this.currentY += 7 + 4;
  }

  private truncate(s: string, max: number): string {
    return s.length > max ? s.slice(0, max - 1) + '…' : s;
  }

  private async addMeasurementPhotos(measurements: Array<{
    photoName: string;
    fileName?: string;
    preview?: string;
    amount: number;
    unit: string;
  }>) {
    this.addSectionHeader('Schadensdokumentation');

    const totalNet = measurements
      .filter((m) => (m.unit ?? 'm²') === 'm²')
      .reduce((sum, m) => sum + m.amount, 0);
    const spacing = 16;
    const captionH = 14;
    // Maximale Bildbox je Spalte; das tatsächliche Bild wird mit dem
    // natürlichen Seitenverhältnis hineinskaliert (wie in der App-Vorschau),
    // damit hochformatige Aufnahmen nicht verzerrt werden.
    const cellW = (this.contentWidth - spacing) / 2;
    const maxImgH = 70;

    type Prepared = {
      m: typeof measurements[number];
      src?: string;
      drawW: number;
      drawH: number;
    };

    const prepare = async (m?: typeof measurements[number]): Promise<Prepared | null> => {
      if (!m) return null;
      const src = m.preview ? await this.compressImage(m.preview) : undefined;
      let drawW = cellW;
      let drawH = maxImgH;
      if (src) {
        const dims = await this.getImageDimensions(src);
        if (dims) {
          const ratio = dims.width / dims.height;
          // Skalierung auf cellW × maxImgH unter Beibehaltung des Seitenverhältnisses
          if (cellW / ratio <= maxImgH) {
            drawW = cellW;
            drawH = cellW / ratio;
          } else {
            drawH = maxImgH;
            drawW = maxImgH * ratio;
          }
        }
      }
      return { m, src, drawW, drawH };
    };

    for (let i = 0; i < measurements.length; i += 2) {
      const left = await prepare(measurements[i]);
      const right = await prepare(measurements[i + 1]);

      const rowImgH = Math.max(left?.drawH ?? 0, right?.drawH ?? 0, 30);
      this.checkPageBreak(rowImgH + captionH + 4);

      const render = (entry: Prepared, x: number) => {
        const { m, src, drawW, drawH } = entry;
        // Bild zentriert in der Zelle platzieren, vertikal an Zellunterkante ausrichten
        const ix = x + (cellW - drawW) / 2;
        const iy = this.currentY + (rowImgH - drawH);
        if (src) {
          try {
            this.pdf.addImage(src, getImageFormat(src), ix, iy, drawW, drawH);
          } catch { /* ignore */ }
        } else {
          this.pdf.setDrawColor(190, 190, 190);
          this.pdf.setFillColor(245, 245, 245);
          this.pdf.rect(ix, iy, drawW, drawH, 'FD');
        }

        const captionY = this.currentY + rowImgH + 6;
        this.pdf.setFontSize(8);
        this.pdf.setFont('helvetica', 'bold');
        this.pdf.setTextColor(20, 20, 20);
        const label = m.fileName ? `${m.photoName}: ${m.fileName}` : m.photoName;
        this.pdf.text(label, x, captionY);

        // Erfasste Menge (mit gespeicherter Einheit) — in Bauwerk-Orange.
        this.pdf.setFont('helvetica', 'bold');
        this.pdf.setTextColor(ORANGE_R, ORANGE_G, ORANGE_B);
        const unitLabel = m.unit ?? 'm²';
        this.pdf.text(`Menge: ${formatDecimal(m.amount, 2)} ${unitLabel}`, x, captionY + 6);
      };

      if (left) render(left, this.margin);
      if (right) render(right, this.margin + cellW + spacing);

      this.currentY += rowImgH + captionH;
    }

    this.checkPageBreak(12);
    this.pdf.setFillColor(TEAL_R, TEAL_G, TEAL_B);
    this.pdf.setTextColor(255, 255, 255);
    this.pdf.roundedRect(this.margin, this.currentY, this.contentWidth, 9, 2, 2, 'F');
    this.pdf.setFontSize(9.5);
    this.pdf.setFont('helvetica', 'bold');
    if (totalNet > 0) {
      this.pdf.text(
        `Gesamtfläche aller Aufmaße (m²): ${formatDecimal(totalNet, 2)} m²`,
        this.margin + 4, this.currentY + 6
      );
    } else {
      this.pdf.text(
        `Aufmaße (${measurements.length} Foto${measurements.length === 1 ? '' : 's'})`,
        this.margin + 4, this.currentY + 6
      );
    }
    this.currentY += 9 + 8;
  }

  /**
   * Liest die natürlichen Pixelmaße eines Bildes (Data-URL oder URL) aus.
   * Wird für die seitenverhältnisgerechte Skalierung der Aufmaß-Fotos genutzt.
   */
  private getImageDimensions(src: string): Promise<{ width: number; height: number } | null> {
    return new Promise((resolve) => {
      try {
        const img = new Image();
        img.onload = () => resolve({ width: img.naturalWidth || img.width, height: img.naturalHeight || img.height });
        img.onerror = () => resolve(null);
        img.src = src;
      } catch {
        resolve(null);
      }
    });
  }

  private async addPhotos(photos: Array<{ preview?: string; file: File; displayName?: string; description?: string }>) {
    if (this.documentType === "invoice") return this.addInvoicePhotos(photos);
    this.addSectionHeader('Projektfotos');

    const imgW = 75;
    const imgH = 55;
    const spacing = 16;

    for (let i = 0; i < photos.length; i += 2) {
      const p1 = photos[i];
      const p2 = photos[i + 1];

      this.pdf.setFontSize(8);
      this.pdf.setFont('helvetica', 'normal');
      const captions = [p1, p2].map((photo, index) => {
        if (!photo) return [];
        const title = photo.displayName?.trim() || `Foto ${i + index + 1}: ${photo.file.name}`;
        const lines = this.pdf.splitTextToSize(title, imgW) as string[];
        return lines.slice(0, 2);
      });
      const captionH = Math.max(...captions.map((lines) => lines.length)) * 3.5;
      this.checkPageBreak(imgH + captionH + 14);

      const c1 = p1?.preview ? await this.compressImage(p1.preview) : undefined;
      const c2 = p2?.preview ? await this.compressImage(p2.preview) : undefined;

      const renderPhoto = (p: typeof photos[number], x: number, idx: number, src?: string) => {
        if (src) {
          try {
            this.pdf.addImage(src, getImageFormat(src), x, this.currentY, imgW, imgH);
          } catch { /* ignore */ }
        } else {
          this.pdf.setDrawColor(190, 190, 190);
          this.pdf.setFillColor(245, 245, 245);
          this.pdf.rect(x, this.currentY, imgW, imgH, 'FD');
        }
        this.pdf.setFontSize(8);
        this.pdf.setFont('helvetica', 'normal');
        this.pdf.setTextColor(60, 60, 60);
        const labelLines = captions[idx - i];
        labelLines.forEach((line, lineIndex) => {
          this.pdf.text(line, x, this.currentY + imgH + 7 + lineIndex * 3.5);
        });
      };

      if (p1) renderPhoto(p1, this.margin, i, c1);
      if (p2) {
        const rx = this.margin + imgW + spacing;
        if (rx + imgW <= this.margin + this.contentWidth) {
          renderPhoto(p2, rx, i + 1, c2);
        }
      }

      this.currentY += imgH + Math.max(20, captionH + 14);
    }
  }

  private async addInvoicePhotos(photos: NonNullable<QuoteData["photos"]>) {
    const gap = 10;
    const cellWidth = (this.contentWidth - gap) / 2;
    const overflow: { number: number; title: string; description: string }[] = [];
    for (let start = 0; start < photos.length; start += 4) {
      if (start > 0) {
        this.pdf.addPage();
        this.currentY = this.margin;
      }
      this.addSectionHeader('Fotodokumentation der abgerechneten Leistungen');
      const gridTop = this.currentY;
      const cellHeight = (this.pageHeight - 34 - gridTop - gap) / 2;
      for (let slot = 0; slot < 4 && start + slot < photos.length; slot++) {
        const photo = photos[start + slot];
        const number = start + slot + 1;
        if (!photo.preview) throw new Error(`Rechnungsfoto „${photo.file.name}“ fehlt.`);
        const image = await this.compressImage(photo.preview);
        if (!image) throw new Error(`Rechnungsfoto „${photo.file.name}“ konnte nicht verarbeitet werden.`);
        const dimensions = await this.getImageDimensions(image);
        if (!dimensions?.width || !dimensions.height) {
          throw new Error(`Rechnungsfoto „${photo.file.name}“ ist nicht lesbar.`);
        }
        const title = photo.displayName?.trim() || photo.file.name;
        const description = photo.description?.trim() !== title ? photo.description?.trim() || "" : "";
        this.pdf.setFont("helvetica", "bold");
        this.pdf.setFontSize(8);
        let titleLines = this.pdf.splitTextToSize(`Foto ${number}: ${title}`, cellWidth) as string[];
        this.pdf.setFont("helvetica", "normal");
        let descriptionLines = description ? this.pdf.splitTextToSize(description, cellWidth) as string[] : [];
        let captionHeight = titleLines.length * 4 + descriptionLines.length * 3.8;
        // Keep four photos per page even for exceptionally long descriptions,
        // preserving their full text on separate, numbered continuation pages.
        if (captionHeight > cellHeight - 37) {
          overflow.push({ number, title, description });
          titleLines = [`Foto ${number}`];
          descriptionLines = ["Text siehe Bildbeschreibungen."];
          captionHeight = 7.8;
        }
        const imageBoxHeight = Math.min(85, cellHeight - captionHeight - 7);
        const scale = Math.min(cellWidth / dimensions.width, imageBoxHeight / dimensions.height);
        const width = dimensions.width * scale;
        const height = dimensions.height * scale;
        const x = this.margin + (slot % 2) * (cellWidth + gap);
        const y = gridTop + Math.floor(slot / 2) * (cellHeight + gap);
        this.pdf.addImage(image, getImageFormat(image),
          x + (cellWidth - width) / 2, y + (imageBoxHeight - height) / 2, width, height);
        let textY = y + imageBoxHeight + 5;
        this.pdf.setTextColor(40, 40, 40);
        this.pdf.setFont("helvetica", "bold");
        for (const line of titleLines) {
          this.pdf.text(line, x, textY);
          textY += 4;
        }
        this.pdf.setFont("helvetica", "normal");
        for (const line of descriptionLines) {
          this.pdf.text(line, x, textY);
          textY += 3.8;
        }
        this.currentY = Math.max(this.currentY, textY + 3);
      }
    }
    if (overflow.length) {
      this.pdf.addPage();
      this.currentY = this.margin;
      this.addSectionHeader("Bildbeschreibungen");
      for (const entry of overflow) {
        const paragraph = (text: string, bold: boolean) => {
          this.pdf.setFont("helvetica", bold ? "bold" : "normal");
          this.pdf.setFontSize(9);
          this.pdf.setTextColor(40, 40, 40);
          for (const line of this.pdf.splitTextToSize(text, this.contentWidth) as string[]) {
            this.checkPageBreak(5);
            this.pdf.text(line, this.margin, this.currentY);
            this.currentY += 4.5;
          }
          this.currentY += 3;
        };
        paragraph(`Foto ${entry.number}: ${entry.title}`, true);
        if (entry.description) paragraph(entry.description, false);
      }
    }
  }

  private addAnalysisResults(results: Array<{ material: string; condition: string; recommendations: string[] }>) {
    this.addSectionHeader('KI-Analyse Ergebnisse');

    results.forEach((result, index) => {
      this.checkPageBreak(14 + result.recommendations.length * 5);

      this.pdf.setFillColor(248, 251, 251);
      this.pdf.setDrawColor(MINT_R, MINT_G, MINT_B);
      this.pdf.setLineWidth(0.3);
      const blockH = 12 + result.recommendations.length * 5;
      this.pdf.roundedRect(this.margin, this.currentY, this.contentWidth, blockH, 2, 2, 'FD');

      this.pdf.setFontSize(8.5);
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.setTextColor(TEAL_R, TEAL_G, TEAL_B);
      this.pdf.text(`Analyse ${index + 1}`, this.margin + 4, this.currentY + 6);

      this.pdf.setTextColor(40, 40, 40);
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.setFontSize(8);
      this.pdf.text(`Material: ${result.material}`, this.margin + 45, this.currentY + 6);
      this.pdf.text(`Zustand: ${result.condition}`, this.margin + 115, this.currentY + 6);

      let ry = this.currentY + 11.5;
      result.recommendations.forEach(rec => {
        this.pdf.setTextColor(60, 60, 60);
        this.pdf.text(`• ${rec}`, this.margin + 8, ry);
        ry += 5;
      });

      this.currentY += blockH + 5;
    });
  }

  private addNotes(notes: string) {
    this.addSectionHeader('Hinweise & Bemerkungen');

    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setFontSize(9);
    this.pdf.setTextColor(50, 50, 50);

    const lines = this.pdf.splitTextToSize(notes, this.contentWidth - 4);
    lines.forEach((line: string) => {
      this.checkPageBreak(6);
      this.pdf.text(line, this.margin + 2, this.currentY);
      this.currentY += 5;
    });

    this.currentY += 6;
  }

  private addFooterAllPages() {
    const totalPages = this.pdf.getNumberOfPages();
    const cx = this.pageWidth / 2;

    // Schlanker Hofer-Footer ohne Partner-Logos: zentrierter Markenname,
    // dreizeiliger Kontaktblock, Seitenzahl rechts. Auf jeder Seite identisch.
    const brandY = this.pageHeight - 22;
    const logoH = 9;
    const logoW = this.hoferLogoAspect ? logoH * this.hoferLogoAspect : 30;

    for (let p = 1; p <= totalPages; p++) {
      this.pdf.setPage(p);

      this.pdf.setDrawColor(TEAL_R, TEAL_G, TEAL_B);
      this.pdf.setLineWidth(0.5);
      this.pdf.line(this.margin, this.pageHeight - 28, this.pageWidth - this.margin, this.pageHeight - 28);

      // Hofer-Logo links, Markentext rechts daneben.
      let textStartX = this.margin;
      if (this.hoferLogoDataUrl) {
        try {
          this.pdf.addImage(
            this.hoferLogoDataUrl,
            getImageFormat(this.hoferLogoDataUrl),
            this.margin,
            brandY - logoH + 1.5,
            logoW,
            logoH,
          );
          textStartX = this.margin + logoW + 4;
        } catch {
          /* ignore; Markentext steht trotzdem links */
        }
      }
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.setFontSize(8);
      this.pdf.setTextColor(SLATE_R, SLATE_G, SLATE_B);
      this.pdf.text(`${COMPANY.name} · ${COMPANY.legalName}`, textStartX, brandY - 2);
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.setFontSize(6.8);
      this.pdf.setTextColor(SLATE_R, SLATE_G, SLATE_B);
      const footerTagline = this.pdf.splitTextToSize(COMPANY.tagline, this.pageWidth - this.margin - textStartX);
      this.pdf.text(footerTagline, textStartX, brandY + 1.4);

      this.pdf.setDrawColor(200, 210, 210);
      this.pdf.setLineWidth(0.2);
      this.pdf.line(this.margin, this.pageHeight - 14, this.pageWidth - this.margin, this.pageHeight - 14);

      // Adresse, Telefon und E-Mail werden bewusst NICHT mehr im
      // Seitenfuß wiederholt – sie stehen einmalig im Briefkopf-Sender
      // (siehe `addAddressSection`) und im Footer-Block am Ende des
      // Angebotstextes. Der Seitenfuß zeigt nur Marke und Seitenzahl.
      const lastLineY = this.pageHeight - 8;
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.setFontSize(6.5);
      this.pdf.setTextColor(140, 140, 140);
      this.pdf.text(
        `Seite ${p} / ${totalPages}`,
        this.pageWidth - this.margin,
        lastLineY,
        { align: 'right' }
      );
    }
  }

  private async compressImage(src: string, maxSize = 1024, quality = 0.8): Promise<string> {
    try {
      return await compressImageSrc(src, maxSize, quality);
    } catch (error) {
      console.warn("Bildkomprimierung fehlgeschlagen, nutze Original:", error);
      return src;
    }
  }
}
